<template>
  <div class="content">
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells position="between">
        <app-cells :indent="false">
          <h1 class="title">Order (ID: {{ $route.params.id }})</h1>
          <div class="status-title" :class="[`status-title--${ order_data.status.code }`]">
            {{ order_data.status.name }}
          </div>
        </app-cells>
        <app-button v-if="order_data.status.code === 'draft'" @click.native="onShowModalDelete(null)" theme="error" ref="delete_order">
          Delete
        </app-button>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Date:</span>
              <span>{{ dateFormat(order_data.date) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Storekeeper:</span>
              <router-link
                v-if="is_general_manager"
                :to="{ name: 'employees-profile', params: { id: order_data.storekeeper.id } }"
                class="info-cards__link"
              >
                {{ order_data.storekeeper.full_name }}
              </router-link>
              <span v-else>{{ order_data.storekeeper.full_name }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Buyer:</span>
              <router-link
                v-if="is_general_manager"
                :to="{ name: 'employees-profile', params: { id: order_data.buyer.id } }"
                class="info-cards__link"
              >
                {{ order_data.buyer.full_name }}
              </router-link>
              <span v-else>{{ order_data.buyer.full_name }}</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row info-cards__row--dotted">
            <span>Estimated value:</span><span>{{ amountFormat(order_data.estimated) }} TSh</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>Real value:</span><span>{{ amountFormat(order_data.real) }} TSh</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>Difference value:</span><span>{{ amountFormat(order_data.difference) }} TSh</span>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Supplier:</span>
              <router-link
                v-if="is_general_manager || is_product_storage_manager || is_product_storage_supervisor"
                :to="{ name: 'storage-supplier-prices-detail', params: { id: order_data.supplier.id } }"
                class="info-cards__link"
              >
                {{ order_data.supplier.trade_name }}
              </router-link>
              <span v-else>{{ order_data.supplier.trade_name }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Payment method:</span> <span>{{ order_data.supplier_payment_method.public_name || '-' }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Warehouse:</span> <span>{{ order_data.warehouse?.name || '-' }}</span>
            </div>
          </div>
        </div>
      </div>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default table-default--orders-list"
      >
        <template #h__item><div class="table-default__left">Item</div></template>
        <template #count="{ row, index }">
          {{ !['draft', 'in_process'].includes(order_data.status.code) ? index : index !== table.items.length ? index : '' }}
        </template>
        <template #item="{ row, index }">
          <router-link
            v-if="row.item && (!['draft', 'in_process'].includes(order_data.status.code) || (['draft', 'in_process'].includes(order_data.status.code) && index !== table.items.length))"
            :to="{ name: 'storage-items-detail', params: { id: row.item.id } }"
            class="table-default__link-border"
          >
            {{ row.item.public_name }}
          </router-link>
          <app-select
            v-if="['draft', 'in_process'].includes(order_data.status.code) && index === table.items.length"
            @option:selected="onNewItemChange"
            v-model="new_row"
            :options="item_options"
            :clearable="false"
            :calculate-position="withPopper"
            append-to-body
            placeholder="Enter item name"
            class="select--200"
            ref="new_item"
            :tabindex="1"
          />
        </template>
        <template #variant="{ row, index }">
          <span v-if="!['draft', 'in_process'].includes(order_data.status.code) || (['draft', 'in_process'].includes(order_data.status.code) && index !== table.items.length)">
            {{ row.variant?.public_name || '-' }}
          </span>
          <app-select
            v-if="index === table.items.length && new_row && variant_options.length"
            @option:selected="onNewVariantChange"
            v-model="new_row.variant"
            :options="variant_options"
            :calculate-position="withPopper"
            append-to-body
            placeholder="Enter variant name"
            class="select--200"
            ref="new_variant"
            :tabindex="2"
          />
        </template>
        <template #item.stock="{ row, index }">
          <span
            v-tooltip="{content: `${row.variant?.stock ? 'Item / Variant' : ''}`, placement: 'top-start', offset: 10}"
          >
            {{ !['draft', 'in_process'].includes(order_data.status.code) && row.item ? stockStringFormat(row.item.stock, row.variant?.stock) : index !== table.items.length ? stockStringFormat(row.item.stock, row.variant?.stock) : new_row ? stockStringFormat(new_row.stock, new_row.variant?.stock) : '' }}
          </span>
        </template>
        <template #estimated_quantity="{ row, index }">
          <template v-if="order_data.status.code === 'draft'">
            <input
              v-if="index !== table.items.length"
              v-model.number="row.estimated_quantity"
              class="input input--150"
              @change="updateTableData(row.id, { estimated_quantity: row.estimated_quantity })"
              type="number"
              style="text-align: center"
            />
            <input
              v-if="index === table.items.length && new_row"
              @keydown.enter="onNewRowSubmit"
              v-model.number="new_row.estimated_quantity"
              class="input input--150"
              type="number"
              style="text-align: center"
              ref="new_qty"
              :tabindex="3"
            />
          </template>
          <template v-else>
            <template v-if="order_data.status.code === 'in_process'">
              <template v-if="index !== table.items.length">
                {{ amountFormat(row.estimated_quantity) }}
              </template>
              <template v-if="index === table.items.length && new_row">0</template>
            </template>
            <template v-else>
              {{ amountFormat(row.estimated_quantity) }}
            </template>
          </template>
        </template>
        <template #estimated_price="{ row, index }">
          <template v-if="['draft', 'in_process'].includes(order_data.status.code)">
            <template v-if="order_data.status.code === 'draft'">
              <input
                v-if="index !== table.items.length"
                v-model.number="row.estimated_price"
                class="input input--150"
                @change="updateTableData(row.id, { estimated_price: row.estimated_price })"
                type="number"
                style="text-align: center"
              />
              <input
                v-if="index === table.items.length && new_row"
                @keyup.enter="onNewRowSubmit"
                v-model.number="new_row.price"
                class="input input--150"
                type="number"
                style="text-align: center"
                ref="new_price"
                :tabindex="4"
              />
            </template>
            <template v-if="order_data.status.code === 'in_process'">
              <template v-if="index !== table.items.length">{{ amountFormat(row.estimated_price) }}</template>
              <template v-if="index === table.items.length && new_row">{{ amountFormat(new_row.price) }}</template>
            </template>
          </template>
          <template v-else>
            {{ amountFormat(row.estimated_price) }}
          </template>
        </template>
        <template #value="{ row, index }">
          <template v-if="order_data.status.code === 'in_process'">
            <template v-if="index !== table.items.length">{{ amountFormat(row.value) }} TSh</template>
            <template v-if="index === table.items.length && new_row">0</template>
          </template>
          <template v-if="order_data.status.code === 'completed'">{{ amountFormat(row.value) }} TSh</template>
        </template>
        <template #controls="{ row, index }">
          <div v-if="index !== table.items.length" class="table-default__controls">
            <button @click="onShowModalDelete(row.id)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="">
            </button>
          </div>
        </template>
        <template #real_quantity="{ row, index }">
          <template v-if="order_data.status.code === 'in_process'">
            <input
              v-if="index !== table.items.length"
              v-model.number="row.real_quantity"
              class="input input--150"
              @change="updateTableData(row.id, { real_quantity: row.real_quantity })"
              type="number"
              style="text-align: center"
            />
            <input
              v-if="index === table.items.length && new_row"
              @keyup.enter="onNewRowSubmit"
              v-model.number="new_row.real_quantity"
              class="input input--150"
              type="number"
              style="text-align: center"
              ref="new_real_qty"
              :tabindex="5"
            />
          </template>
          <template v-else>{{ amountFormat( row.real_quantity) }}</template>
        </template>
        <template #different="{ row, index }">
          <template v-if="order_data.status.code === 'in_process'">
            <template v-if="index !== table.items.length">
              <span
                :class="{
                    'table-default__text-green': row.different < 0,
                    'table-default__text-red': row.different > 0,
                  }"
              >
                {{ row.different }}
              </span>
            </template>
            <template v-if="index === table.items.length && new_row">0</template>
          </template>
          <span
            v-else
            :class="{
                'table-default__text-green': row.different < 0,
                'table-default__text-red': row.different > 0,
              }"
          >
            {{ row.different }}
          </span>
        </template>
        <template #real_price="{ row, index }">
          <template v-if="order_data.status.code === 'in_process'">
            <input
              v-if="index !== table.items.length"
              v-model.number="row.real_price"
              class="input input--150"
              @change="updateTableData(row.id, { real_price: row.real_price })"
              type="number"
              style="text-align: center"
            />
            <input
              v-if="index === table.items.length && new_row"
              @keyup.enter="onNewRowSubmit"
              v-model.number="new_row.price"
              class="input input--150"
              type="number"
              style="text-align: center"
              ref="new_real_price"
              :tabindex="6"
            />
          </template>
          <template v-else>{{ amountFormat(row.real_price) }}</template>
        </template>
      </v-client-table>
      <app-cells position="end">
        <app-button
          v-for="(data, index) in status_buttons[order_data.status.code]"
          :key="index"
          @click.native="onShowModalUpdate(data)"
          :theme="data.modal_type === 'draft' ? 'warn' : 'default'"
        >
          {{ data.button_text }}
        </app-button>
      </app-cells>
    </template>
    <modal
      name="order_update"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideModalUpdate" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Update order
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to {{ modal_data.modal_text }}</div>
          <form v-if="modal_data.modal_type === 'completed'">
            <app-form-group label="Warehouse" required>
              <app-select
                v-model="warehouse"
                :options="warehouse_options"
                :error="$v.warehouse.$error"
                :clearable="false"
                placeholder="Warehouse"
              >
              </app-select>
              <template #error>
                <div v-if="$v.warehouse.$dirty && !$v.warehouse.required">Required field</div>
              </template>
            </app-form-group>
          </form>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onHideModalUpdate" theme="dark">Cancel</app-button>
            <app-button @click.native="onUpdateOrderStatus(modal_data.modal_type)" ref="status_changed" :disabled="$v.$error">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="delete_modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideModalDelete" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete {{ item_to_delete ? 'item' : 'transfer' }}
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to do that?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onHideModalDelete" theme="dark">Cancel</app-button>
            <app-button v-if="item_to_delete" @click.native="onDeleteRow" ref="delete">Yes</app-button>
            <app-button v-else @click.native="onDeleteOrder" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteStorageProductStorageOrdersDetail,
  getStorageProductStorageOrdersDetail,
  getStorageProductStorageOrderItems,
  getStorageItems,
  patchStorageProductStorageOrdersDetail,
  deleteStorageProductStorageOrderItems,
  patchStorageProductStorageOrderItems,
  postStorageProductStorageOrderItems,
  getStorageItemsTypeVariants,
  getStorageStockWarehouses
} from '@/http'
import { format, parseISO } from 'date-fns'
import { createPopper } from '@popperjs/core'
import { required, requiredIf } from 'vuelidate/lib/validators'

export default {
  name: 'StorageProductStorageOrdersDetail',
  data() {
    return {
      page_loaded: false,
      order_data: {},
      item_to_delete: null,
      table: {
        items: [],
        columns: [],
        options: {
          headings: {
            'count': '#',
            'item': 'Item',
            'item.stock': 'Stock',
            'estimated_quantity': 'Est. Qty',
            'estimated_price': 'Est. Price',
            'real_quantity': 'Real qty',
            'different': 'Real diff.',
            'real_price': 'Real price',
            'value': 'Total value',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 1000,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      status_buttons: {
        draft: [ { button_text: 'Ready to buy', modal_type: 'ready', modal_text: 'set status Ready to buy?' } ],
        ready: [
          { button_text: 'Redraft', modal_type: 'draft', modal_text: 'set status Redraft?' },
          { button_text: 'Confirm', modal_type: 'in_process', modal_text: 'set status In process?' }
        ],
        in_process: [ { button_text: 'Complete', modal_type: 'completed', modal_text: 'complete order?' } ]
      },
      columns_way_1: [
        'count',
        'item',
        'variant',
        'item.stock',
        'estimated_quantity',
        'estimated_price',
        'controls'
      ],
      columns_way_2: [
        'count',
        'item',
        'variant',
        'item.stock',
        'estimated_quantity',
        'estimated_price'
      ],
      columns_way_3: [
        'count',
        'item',
        'variant',
        'item.stock',
        'estimated_quantity',
        'estimated_price',
        'real_quantity',
        'different',
        'real_price',
        'value',
      ],
      item_options: [],
      variant_options: [],
      new_row: null,
      supplier_payment_method: 'cash',
      modal_data: {},
      placement: 'bottom',
      warehouse: null,
      warehouse_options: [],
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.some(group => group.code === 'general_manager')
    },
    is_product_storage_manager() {
      return this.$store.state.userGroups.some(group => group.code === 'product_storage_manager')
    },
    is_product_storage_supervisor() {
      return this.$store.state.userGroups.some(group => group.code === 'product_storage_supervisor')
    },
  },
  validations: {
    warehouse: {
      required: requiredIf(function () {
        return this.order_data.status.code === 'in_process'
      })
    },
  },
  async created() {
    document.title = `Order (ID: ${ this.$route.params.id }) | Altezza Board`
    await this.fetchOrderData()
    const response = await Promise.all([
      getStorageItems({supplier: this.order_data.supplier.id}),
      getStorageProductStorageOrderItems(this.$route.params.id),
      getStorageStockWarehouses({section: 'products'})
    ])
    this.item_options = response[0].data
    this.table.items = response[1].data
    this.warehouse_options = response[2].data
    this.changeTableColumns(this.order_data.status.code, true)
    this.page_loaded = true
  },
  methods: {
    async fetchOrderData() {
      const response = await getStorageProductStorageOrdersDetail(this.$route.params.id)
      this.order_data = response.data
      if (response.data.supplier_payment_method?.code) this.supplier_payment_method = response.data.supplier_payment_method.code
    },
    changeTableColumns(status, created = false) {
      if (status === 'draft') {
        this.table.columns = this.columns_way_1
        this.table.items.push({})
      } else if (status === 'ready') {
        if (!created) this.table.items.pop()
        this.table.columns = this.columns_way_2
      } else {
        if (status === 'in_process') this.table.items.push({})
        else {
          if (!created) this.table.items.pop()
        }
        this.table.columns = this.columns_way_3
      }
      this.new_row = null
    },
    postOrderItem(quantity_field, price_field) {
      const data = {}
      if ((this.new_row[quantity_field] && this.new_row.price) && !(this.new_row.item_type?.code === 'variants' && !this.new_row.variant)) {
        data.item = this.new_row.id
        data[quantity_field] = this.new_row[quantity_field]
        data[price_field] = this.new_row.price
        if (this.new_row.item_type.code === 'variants') data.variant = this.new_row.variant?.id
        postStorageProductStorageOrderItems(this.$route.params.id, data)
          .then(res => {
            this.table.items = res.data
            this.table.items.push({})
            this.new_row = null
            this.$nextTick(() => {
              this.$refs.new_item.$el.querySelector('input').focus()
            })
            this.$notify({
              type: 'success',
              title: 'Item added',
            })
          })
      } else {
        this.$notify({
          type: 'info',
          title: 'Check that the table row fields are filled in correctly',
        })
      }
    },
    onNewRowSubmit() {
      if (this.order_data.status.code === 'draft') {
        this.postOrderItem('estimated_quantity', 'estimated_price')
      } else {
        this.postOrderItem('real_quantity', 'real_price')
      }
    },
    onNewItemChange(e) {
      this.variant_options = []
      if (e.item_type.code === 'variants') {
        getStorageItemsTypeVariants(e.id)
          .then(response => {
            this.variant_options = response.data
            this.$nextTick(() => { this.$refs.new_variant.$el.querySelector('input').focus() })
          })
      } else {
        if (this.order_data.status.code === 'draft') this.$nextTick(() => { this.$refs.new_qty.focus() })
        else this.$nextTick(() => { this.$refs.new_real_qty.focus() })
      }
    },
    onNewVariantChange() {
      if (this.order_data.status.code === 'draft') this.$nextTick(() => { this.$refs.new_qty.focus() })
      else this.$nextTick(() => { this.$refs.new_real_qty.focus() })
    },
    async updateTableData(product_id, data) {
      const response = await patchStorageProductStorageOrderItems(this.$route.params.id, product_id, data)
      this.table.items = response.data
      if (['draft', 'in_process'].includes(this.order_data.status.code)) this.table.items.push({})
      await this.fetchOrderData()
    },
    onShowModalUpdate(data) {
      this.modal_data = data
      this.warehouse = null
      this.$v.$reset()
      this.$modal.show('order_update')
    },
    onHideModalUpdate() {
      this.$modal.hide('order_update')
    },
    async onUpdateOrderStatus(status) {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.$refs.status_changed.preload = true
        const data = { status }
        if (status === 'completed') data.warehouse = this.warehouse.id
        await patchStorageProductStorageOrdersDetail(this.$route.params.id, data)
        this.$refs.status_changed.preload = false
        this.onHideModalUpdate()
        this.$notify({
          type: 'success',
          title: 'Status changed',
        })
        await this.fetchOrderData()
        this.changeTableColumns(this.order_data.status.code)
      }
    },
    onShowModalDelete(id) {
      if (id) this.item_to_delete = id
      this.$modal.show('delete_modal')
    },
    onHideModalDelete() {
      this.item_to_delete = null
      this.$modal.hide('delete_modal')
    },
    async onDeleteRow() {
      this.$refs.delete.preload = true
      const response = await deleteStorageProductStorageOrderItems(this.$route.params.id, this.item_to_delete)
      this.table.items = response.data
      this.table.items.push({})
      this.new_row = null
      this.$notify({
        type: 'success',
        title: 'Item deleted',
      })
      this.onHideModalDelete()
      await this.fetchOrderData()
    },
    onDeleteOrder() {
      this.$refs.delete.preload = true
      deleteStorageProductStorageOrdersDetail(this.$route.params.id)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Order deleted',
          })
          this.$router.push({ name: 'storage-product-storage-orders' })
        })
        .catch(() => { this.$refs.delete.preload = false })
    },
    amountFormat(amount) {
      return amount ? Intl.NumberFormat('ru-RU').format(amount) : ''
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    stockStringFormat(stock, variant_stock) {
      return `${this.amountFormat(stock)} ${variant_stock ? `/ ${this.amountFormat(variant_stock)}` : ''}`
    },
    withPopper (dropdownList, component, {width}) {
      dropdownList.style.width = width
      createPopper(component.$refs.toggle, dropdownList, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 0],
              adaptive: true
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn({state}) {
              component.$el.classList.toggle('drop-up', state.placement === 'top')
            },
          },
        ]
      })
    }
  }
}
</script>